import { FC, useEffect, useMemo, useState } from 'react';

import { Box, Button } from '@mui/material';
import { useChannel } from 'ably/react';

import { CfsApi, UnitApi } from '@/api';
import { ActionButton, ButtonWithDoubleClickPrevention } from '@/components';
import { DraggableUnit } from '@/components/DragAndDrop/DraggableUnit';
import { UnitAgencyActionModal } from '@/components/Modals/UnitShiftListModal/components/UnitAgencyActionModal';
import { AssignedUnitShiftItem } from '@/contexts';
import { withLoader } from '@/hocs';
import { useCFSContext, useUnitListFilter, useUnitShiftContext } from '@/hooks';
import { IUnit, IUnitShift, Role } from '@/models';
import {
  canModifyCFS,
  getAccountHeadquarters,
  getUnitShiftAddressLabel,
  isCFSViewOnly,
} from '@/services';
import {
  ConfirmTypes,
  useAgencyStore,
  useToastStore,
  useUserStore,
} from '@/store';

import { UnitAccordion } from './UnitAccordion';
import { UnitItem } from './UnitItem';
import {
  default as UnitShiftAssignModal,
  UnitShiftAssignModalProps,
} from './UnitShiftAssignModal';
import { CommonUnitsProps } from './UnitShiftList';
import { getAvailableUnitActions, getHeadquarterNameFromAction } from '../data';

interface AvailableUnitsProps extends CommonUnitsProps {
  fetchActiveCFSList?: () => void;
  handleAssignUnit?: () => void;
  showMenu?: boolean;
  showDraggableUnits?: boolean;
}

const accordions = {
  WAITING: 'waiting',
  ON_DUTY: 'on-duty',
  STANDBY: 'standby',
  SCHEDULED: 'scheduled',
};

type ExpandedAccordion = {
  scheduled: string | null;
  waiting: string | null;
  'on-duty': string | null;
  standby: string | null;
};

const isUnitShift = (
  item: IUnit | IUnitShift | AssignedUnitShiftItem,
): item is IUnitShift => {
  return (item as IUnitShift).startAt !== undefined;
};

const AvailableUnits: FC<AvailableUnitsProps> = ({
  cfsId,
  filterValues,
  searchText,
  showLoader,
  hideLoader,
  fetchActiveCFSList,
  handleAssignUnit,
  showMenu,
  showDraggableUnits,
}) => {
  const { updateToast } = useToastStore();
  const { cfs, unitShiftDistances, fetchCFS } = useCFSContext();
  const {
    onDutyUnitShifts,
    waitingUnitShifts,
    scheduledUnitShifts,
    unitShiftAddresses,
    fetchOnDutyUnitShifts,
    fetchScheduledUnitShifts,
    fetchAllActiveUnitShifts,
    fetchAssignedUnitShifts,
  } = useUnitShiftContext();
  const { account } = useUserStore();
  const headquarters = useMemo(
    () => getAccountHeadquarters(account),
    [account],
  );
  const [expandedPanel, setExpandedPanel] = useState<ExpandedAccordion>({
    waiting: null,
    scheduled: null,
    'on-duty': null,
    standby: null,
  });

  const [standByUnits, setStandByUnits] = useState<IUnit[]>([]);
  const [assignUnitShiftModal, setAssignUnitShiftModal] = useState<
    Omit<UnitShiftAssignModalProps, 'onClose' | 'showLoader' | 'hideLoader'>
  >({
    open: false,
  });
  const { user } = useUserStore();
  const [
    showAssignUnitToUnassignedAgency,
    setShowAssignUnitToUnassignedAgency,
  ] = useState(false);
  const [unitAgencyToAssign, setUnitAgencyToAssign] = useState<
    string | undefined
  >('');
  const [unitShiftToAssign, setUnitShiftToAssign] = useState<IUnitShift>();
  const [headquarterNameToAssign, setHeadquarterNameToAssign] = useState<
    string | null
  >(null);
  const { agencies } = useAgencyStore();

  useChannel(`account:${account?._id}:unit`, () => {
    fetchStandByUnits();
  });

  const activeUnitShiftData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: onDutyUnitShifts,
    searchText,
    searchResources: true,
  }) as IUnitShift[];

  const scheduledUnitsData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: scheduledUnitShifts,
    searchText,
  }) as IUnitShift[];

  const standByUnitsData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: standByUnits,
    searchText,
  }) as IUnit[];
  const waitingUnitShiftData = useUnitListFilter({
    filterValues,
    cfs,
    unitData: waitingUnitShifts,
    searchText,
  }) as IUnitShift[];

  const unitShiftSortFn = (a: IUnitShift, b: IUnitShift) => {
    if (!cfs?.assignedAgencies) return 0; // no sort if no assignedAgencies

    const aHasAgency = cfs.assignedAgencies.includes(a.unit.agency);
    const bHasAgency = cfs.assignedAgencies.includes(b.unit.agency);

    return Number(bHasAgency) - Number(aHasAgency);
  };

  const unitSortFn = (a: IUnit, b: IUnit) => {
    if (!cfs?.assignedAgencies) return 0; // no sort if no assignedAgencies

    const aHasAgency = cfs.assignedAgencies.includes(a.agency);
    const bHasAgency = cfs.assignedAgencies.includes(b.agency);

    return Number(bHasAgency) - Number(aHasAgency);
  };

  const sortedWaitingUnits = useMemo(() => {
    return waitingUnitShiftData.sort(unitShiftSortFn);
  }, [waitingUnitShiftData, cfs?.assignedAgencies]);

  const sortedActiveUnits = useMemo(() => {
    return activeUnitShiftData.sort(unitShiftSortFn);
  }, [activeUnitShiftData, cfs?.assignedAgencies]);

  const sortedScheduledUnits = useMemo(() => {
    return scheduledUnitsData.sort(unitShiftSortFn);
  }, [scheduledUnitsData, cfs?.assignedAgencies]);

  const sortedStandByUnits = useMemo(() => {
    return standByUnitsData.sort(unitSortFn);
  }, [standByUnitsData, cfs?.assignedAgencies]);

  useEffect(() => {
    Promise.all([fetchStandByUnits(), fetchAllActiveUnitShifts()]).then(
      ([standByUnitsRes, { active, waiting }]) => {
        if (waiting.length) {
          setExpandedPanel((prev) => ({
            ...prev,
            waiting: accordions.WAITING,
          }));
        }
        if (active.length) {
          setExpandedPanel((prev) => ({
            ...prev,
            'on-duty': accordions.ON_DUTY,
          }));
        }
        if (standByUnitsRes.length) {
          setExpandedPanel((prev) => ({
            ...prev,
            standby: accordions.STANDBY,
          }));
        }
      },
    );
  }, []);

  // Expand the panels that have data when search text is entered
  useEffect(() => {
    setExpandedPanel({
      waiting: waitingUnitShiftData.length > 0 ? accordions.WAITING : null,
      'on-duty': activeUnitShiftData.length > 0 ? accordions.ON_DUTY : null,
      standby: standByUnitsData.length > 0 ? accordions.STANDBY : null,
      scheduled: scheduledUnitsData.length > 0 ? accordions.SCHEDULED : null,
    });
  }, [waitingUnitShiftData, activeUnitShiftData, standByUnitsData]);

  const fetchStandByUnits = async () => {
    try {
      const filter = JSON.stringify({
        status: 'OFF_DUTY',
        isStandByWhenOffDuty: true,
      });
      const res = await UnitApi.getUnits({
        filter,
        limit: 200,
        skipAgencyHeader: user?.roles.some((role) =>
          role.role.includes(Role.DISPATCHER),
        ),
      });
      setStandByUnits(res.data.results);
      return res.data.results;
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
      return [];
    }
  };

  const handleExpand = (panel: string, accordion: keyof ExpandedAccordion) => {
    if (expandedPanel[accordion] !== null) {
      setExpandedPanel((prev) => ({ ...prev, [panel]: null }));
    } else setExpandedPanel((prev) => ({ ...prev, [panel]: panel }));
  };

  const updateToOffDuty = async (unitId: string) => {
    try {
      await UnitApi.setUnitOffDuty(unitId);
      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Unit shift is now off-duty',
      });
      handleAssignUnit?.();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const cancelScheduledShift = async (unitShiftId: string) => {
    showLoader();
    try {
      await UnitApi.cancelScheduledUnitShift(unitShiftId);
      await Promise.all([fetchScheduledUnitShifts(), fetchOnDutyUnitShifts()]);
      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Scheduled shift has been cancelled',
      });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const handleSelectAction = (
    action: string | number,
    unitShiftId?: string,
    unitId?: string,
  ) => {
    const headquarterName = getHeadquarterNameFromAction(action);
    if (action === 'assign') {
      setAssignUnitShiftModal({
        open: true,
        unitShiftId,
      });
    } else if (headquarterName) {
      setAssignUnitShiftModal({
        open: true,
        unitShiftId,
        assignToHQ: true,
        headquarterName,
      });
    } else {
      updateToOffDuty(unitId || '');
    }
  };

  const handleSelectStandbyAction = (action: string | number, unit: IUnit) => {
    const headquarterName = getHeadquarterNameFromAction(action);
    if (headquarterName) {
      onOpenModal(unit, true, false, headquarterName);
      return;
    }

    switch (action) {
      case 'assign':
        onOpenModal(unit);
        break;
      case 'setOnDuty':
        onOpenModal(unit, false, true);
        break;
    }
  };

  const onCloseModal = (refresh?: boolean) => {
    setAssignUnitShiftModal({
      open: false,
    });
    if (refresh) {
      fetchOnDutyUnitShifts();
    }
  };

  const onOpenModal = (
    unit?: IUnit,
    assignToHQ?: boolean,
    setOnDuty?: boolean,
    headquarterName?: string | null,
  ) => {
    setAssignUnitShiftModal({
      open: true,
      unitShiftId: undefined,
      unit,
      assignToHQ,
      headquarterName,
      setOnDuty,
      handleAssignUnit,
    });
  };

  const onOpenEditModal = (unitShift: IUnitShift) => {
    setAssignUnitShiftModal({
      open: true,
      unit: {
        ...unitShift.unit,
        vehicles: unitShift.vehicles?.map((v) => v._id),
        users: unitShift.users?.map((u) => u._id),
        equipments: unitShift.equipments?.map((e) => e._id),
      },
      unitShiftId: unitShift._id,
      isEditMode: true,
      setOnDuty: true,
    });
  };

  const assignUnitShift = async (
    unitShiftId = '',
    isDispatchedToHQ?: boolean,
    headquarterName?: string | null,
  ) => {
    showLoader();
    try {
      if (cfsId) {
        await CfsApi.assignUnitShift(
          cfsId,
          unitShiftId,
          isDispatchedToHQ,
          headquarterName,
        );
        await Promise.all([
          fetchCFS(),
          fetchAllActiveUnitShifts(),
          fetchAssignedUnitShifts(),
        ]);

        if (fetchActiveCFSList) {
          fetchActiveCFSList();
        }

        handleAssignUnit?.();

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Unit shift successfully assigned',
        });
      }
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message,
        type: ConfirmTypes.WARNING,
      });
    } finally {
      hideLoader();
    }
  };

  const checkAndUpdateShifts = async () => {
    const currentTime = new Date();
    const filterPassed = scheduledUnitsData.filter((unitData) => {
      if (isUnitShift(unitData)) {
        return (
          unitData.status === 'SCHEDULED' &&
          new Date(unitData.startAt as string).getTime() <=
            currentTime.getTime()
        );
      }
      return false;
    });

    if (filterPassed.length) {
      await fetchScheduledUnitShifts();
      await fetchOnDutyUnitShifts();
    }

    return;
  };

  const checkAndAssignAvailableUnitShift = (
    unitShift: IUnitShift,
    headquarterName?: string | null,
  ) => {
    if (cfs?.assignedAgencies?.includes(unitShift.unit.agency)) {
      assignUnitShift(unitShift._id, Boolean(headquarterName), headquarterName);
      return;
    }

    if (agencies) {
      const foundAgency = agencies.find(
        (agency) => agency._id === unitShift.unit.agency,
      );

      setUnitAgencyToAssign(foundAgency?.name);
      setShowAssignUnitToUnassignedAgency(true);
      setUnitShiftToAssign(unitShift);
      setHeadquarterNameToAssign(headquarterName ?? null);
    }
  };

  useEffect(() => {
    checkAndUpdateShifts();

    // Every minute, check for scheduled unit shifts
    const interval = setInterval(checkAndUpdateShifts, 10000);
    return () => clearInterval(interval);
  }, [scheduledUnitsData]);

  const renderHeadquarterButtons = (
    isDispatchToHQEnabled: boolean | undefined,
    onSelect: (headquarterName: string) => void,
  ) => {
    if (!isDispatchToHQEnabled) return null;

    return headquarters.map((headquarter) => (
      <Button
        key={headquarter.name}
        variant="outlined"
        color="inherit"
        size="small"
        onClick={() => onSelect(headquarter.name)}
        disabled={cfs?.isClosed}
        sx={{ mr: 2, padding: '8px 20px' }}
      >
        Assign to {headquarter.name}
      </Button>
    ));
  };

  const renderSecondaryActionMenu = (
    items: ReturnType<typeof getAvailableUnitActions>,
    onSelectItem: (action: string | number) => void,
  ) => (
    <ActionButton
      color="error"
      variant="contained"
      size="small"
      sx={{ ml: 0 }}
      items={items}
      onSelectItem={onSelectItem}
    >
      More actions
    </ActionButton>
  );

  const renderAvailableUnitAssignmentActions = (unitShift: IUnitShift) => {
    if (!canModifyCFS(user)) return null;

    const headquarterButtons = renderHeadquarterButtons(
      unitShift.unit.type.isDispatchToHQEnabled,
      (headquarterName) => {
        if (cfsId) {
          checkAndAssignAvailableUnitShift(unitShift, headquarterName);
          return;
        }

        setAssignUnitShiftModal({
          open: true,
          unitShiftId: unitShift._id,
          assignToHQ: true,
          headquarterName,
        });
      },
    );

    const moreActionsButton = showMenu ? (
      renderSecondaryActionMenu(
        [
          { label: 'Direct', value: 'assign' },
          { label: 'Set off-duty', value: 'set-off-duty' },
        ],
        (action) =>
          handleSelectAction(action, unitShift._id, unitShift.unit._id),
      )
    ) : (
      <ButtonWithDoubleClickPrevention
        color="error"
        variant="contained"
        size="small"
        onClick={() => assignUnitShift(unitShift._id)}
        isDisabled={cfs?.isClosed}
      >
        Direct
      </ButtonWithDoubleClickPrevention>
    );

    return (
      <>
        {headquarterButtons}
        {moreActionsButton}
      </>
    );
  };

  const renderStandbyAssignmentActions = (unit: IUnit) => {
    if (!canModifyCFS(user)) return null;

    if (!showMenu) {
      return (
        <>
          {renderHeadquarterButtons(
            unit.type.isDispatchToHQEnabled,
            (headquarterName) =>
              onOpenModal(unit, true, false, headquarterName),
          )}
          <ButtonWithDoubleClickPrevention
            color="error"
            variant="contained"
            size="small"
            onClick={() => onOpenModal(unit)}
          >
            Direct
          </ButtonWithDoubleClickPrevention>
        </>
      );
    }

    return (
      <>
        {renderHeadquarterButtons(
          unit.type.isDispatchToHQEnabled,
          (headquarterName) => onOpenModal(unit, true, false, headquarterName),
        )}
        <ActionButton
          color="error"
          size="small"
          variant="contained"
          sx={{ ml: 0 }}
          items={[
            { label: 'Direct', value: 'assign' },
            { label: 'Set on duty', value: 'setOnDuty' },
          ]}
          onSelectItem={(value) => handleSelectStandbyAction(value, unit)}
        >
          More actions
        </ActionButton>
      </>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {!!waitingUnitShiftData.length && (
        <UnitAccordion
          title="Waiting"
          value="waiting"
          badge={waitingUnitShiftData.length}
          currentValue={expandedPanel.waiting}
          handleExpand={(p) => handleExpand(p, 'waiting')}
        >
          {sortedWaitingUnits.map((unitShift, index) => (
            <UnitItem
              key={index}
              {...unitShift.unit}
              agency={agencies.find(
                (agency) => agency._id === unitShift.unit.agency,
              )}
              addressLabel={getUnitShiftAddressLabel(
                unitShift,
                unitShiftAddresses,
              )}
              distance={unitShiftDistances[unitShift?._id as string]}
              resources={{
                polygons: unitShift.polygons,
              }}
            >
              {!canModifyCFS(user) ? null : cfsId ? (
                <>
                  {renderHeadquarterButtons(
                    unitShift.unit.type.isDispatchToHQEnabled,
                    (headquarterName) =>
                      assignUnitShift(unitShift._id, true, headquarterName),
                  )}
                  <ButtonWithDoubleClickPrevention
                    color="error"
                    variant="contained"
                    size="small"
                    onClick={() => assignUnitShift(unitShift._id)}
                    isDisabled={cfs?.isClosed}
                  >
                    Direct
                  </ButtonWithDoubleClickPrevention>
                </>
              ) : (
                renderAvailableUnitAssignmentActions(unitShift)
              )}
            </UnitItem>
          ))}
        </UnitAccordion>
      )}
      {!!activeUnitShiftData.length && (
        <UnitAccordion
          title="On duty"
          value="on-duty"
          badge={activeUnitShiftData.length}
          currentValue={expandedPanel['on-duty']}
          handleExpand={(p) => handleExpand(p, 'on-duty')}
        >
          {activeUnitShiftData.length > 0 && showDraggableUnits
            ? sortedActiveUnits.map((unitShift, index) => (
                <DraggableUnit
                  key={unitShift._id}
                  unit={unitShift.unit}
                  unitShift={unitShift}
                  disabled={cfs?.isClosed || isCFSViewOnly(user)}
                >
                  <UnitItem
                    key={index}
                    {...unitShift.unit}
                    agency={agencies.find(
                      (agency) => agency._id === unitShift.unit.agency,
                    )}
                    addressLabel={getUnitShiftAddressLabel(
                      unitShift,
                      unitShiftAddresses,
                    )}
                    distance={unitShiftDistances[unitShift?._id as string]}
                    resources={{
                      polygons: unitShift.polygons,
                      users: unitShift.users,
                      equipments: unitShift.equipments,
                      vehicles: unitShift.vehicles,
                    }}
                    itemType="on-duty"
                    showResources
                    unitAgency={unitShift.unit.agency}
                    onReallocateClick={() => onOpenEditModal(unitShift)}
                  >
                    {renderAvailableUnitAssignmentActions(unitShift)}
                  </UnitItem>
                </DraggableUnit>
              ))
            : activeUnitShiftData.map((unitShift, index) => (
                <UnitItem
                  key={index}
                  {...unitShift.unit}
                  agency={agencies.find(
                    (agency) => agency._id === unitShift.unit.agency,
                  )}
                  addressLabel={getUnitShiftAddressLabel(
                    unitShift,
                    unitShiftAddresses,
                  )}
                  distance={unitShiftDistances[unitShift?._id as string]}
                  resources={{
                    polygons: unitShift.polygons,
                    users: unitShift.users,
                    equipments: unitShift.equipments,
                    vehicles: unitShift.vehicles,
                  }}
                  itemType="on-duty"
                  showResources
                  onReallocateClick={() => onOpenEditModal(unitShift)}
                >
                  {renderAvailableUnitAssignmentActions(unitShift)}
                </UnitItem>
              ))}
        </UnitAccordion>
      )}
      {!!standByUnitsData.length && (
        <UnitAccordion
          title="Standby"
          value="standby"
          badge={standByUnitsData.length}
          currentValue={expandedPanel.standby}
          handleExpand={(p) => handleExpand(p, 'standby')}
        >
          {sortedStandByUnits.map((unit, index) => (
            <UnitItem
              key={index}
              {...unit}
              agency={agencies.find((agency) => agency._id === unit.agency)}
            >
              {renderStandbyAssignmentActions(unit)}
            </UnitItem>
          ))}
        </UnitAccordion>
      )}
      {!!scheduledUnitsData.length && (
        <UnitAccordion
          title="Scheduled"
          value="scheduled"
          badge={scheduledUnitsData.length}
          currentValue={expandedPanel.scheduled}
          handleExpand={(p) => handleExpand(p, 'scheduled')}
        >
          {sortedScheduledUnits.map((unitShift, index) => (
            <UnitItem
              key={index}
              startAt={unitShift.startAt}
              {...unitShift.unit}
              agency={agencies.find(
                (agency) => agency._id === unitShift.unit.agency,
              )}
              resources={{
                polygons: unitShift.polygons,
                users: unitShift.users,
                equipments: unitShift.equipments,
                vehicles: unitShift.vehicles,
              }}
              itemType="scheduled"
              showResources
            >
              {canModifyCFS(user) && (
                <ButtonWithDoubleClickPrevention
                  color="error"
                  variant="contained"
                  size="small"
                  onClick={() => cancelScheduledShift(unitShift._id as string)}
                >
                  Cancel shift
                </ButtonWithDoubleClickPrevention>
              )}
            </UnitItem>
          ))}
        </UnitAccordion>
      )}
      <UnitShiftAssignModal
        onClose={onCloseModal}
        cfsId={cfsId}
        {...assignUnitShiftModal}
        handleAssignUnit={handleAssignUnit}
      />

      <UnitAgencyActionModal
        open={showAssignUnitToUnassignedAgency}
        mode="assign"
        agencyName={unitAgencyToAssign}
        onCancel={() => setShowAssignUnitToUnassignedAgency(false)}
        onConfirm={() => {
          assignUnitShift(
            unitShiftToAssign?._id,
            Boolean(headquarterNameToAssign),
            headquarterNameToAssign,
          );
          setShowAssignUnitToUnassignedAgency(false);
        }}
      />
    </Box>
  );
};

export default withLoader(AvailableUnits);
